import React, { useState, createContext, useEffect, useContext } from "react";
import { CohearingMessage, CohearingMessageFeeling } from "../../models/CohearingMessage";
import { retry } from "../../utils/async";
import { acknowledgeCohearingMessage, listCohearingMessages } from "./service";
import moment from "moment";
import { TelephonyContext } from "./TelephonyContext";
import { CohearingContext } from "./CohearingContextProvider";

export const CohearingMessageContextProvider: React.FC = ({ children }) => {
  const telephonyContext = useContext(TelephonyContext);
  const cohearingContext = useContext(CohearingContext);
  const [messages, setMessages] = useState<CohearingMessage[]>([]);

  // Number of sessions which are listening to this user or call.
  // While > 0, get messages more frequent (in case they are not delivered via websocket).
  const listenerCount = cohearingContext.activeUserSessions.length;

  function acknowledgeMessage(message: CohearingMessage) {
    retry(() => acknowledgeCohearingMessage(message.id)
      .catch(error => {
        console.error("Error acknowledging cohearing message %s", message.id, error)
        throw error;
      }))
      .then(() => {
        console.log("Acknowledged cohearing message %s", message.id);
      });
  }

  function addMessages(newMessages: CohearingMessage[]) {
    setMessages(messages => {
      const existingMessageIds = messages.map(message => message.id);
      return [...messages, ...newMessages.filter(message => !existingMessageIds.includes(message.id))];
    });

    for (const message of newMessages) {
      acknowledgeMessage(message);
    }

    //  Confetti without text should act immediately.
    if (newMessages.find(message => !message.text && message.feeling === CohearingMessageFeeling.Confetti)) {
      telephonyContext.setState({ isConfettiVisible: true });
    }
  }

  /**
   * Gets unread messages. This is a backup method. The preferred way of getting messages
   * is from RealtimeHandler.
   */
  async function refreshMessages() {
    try {
      const unreadMessages = await listCohearingMessages({
        createdAfter: moment().subtract(1, 'minute').toDate(),
        acknowledged: false
      });

      addMessages(unreadMessages);
    } catch (error) {
      console.error("Error refreshing cohearing messages", error);
    }
  }

  const isEnabled = telephonyContext.isTelephonyWidgetOpen || telephonyContext.isView;

  useEffect(() => {
    if (!isEnabled || !listenerCount) {
      return;
    }

    const refreshId = setInterval(() => {
      if (!document.hidden) {
        refreshMessages();
      }
    }, 2500);

    return () => { clearInterval(refreshId); };
  }, [isEnabled, !listenerCount]);

  return (
    <CohearingMessageContext.Provider value={{
      addMessage: message => {
        addMessages([message]);
      },
      deleteMessage(messageId) {
        setMessages(messages => messages.filter(message => message.id !== messageId));
      },
      updateMessage: (messageId, update) => {
        setMessages(messages => messages.map(message => {
          if (message.id === messageId) {
            return new CohearingMessage({ ...message, ...update });
          }

          return message;
        }))
      },
      messages
    }}>
      {children}
    </CohearingMessageContext.Provider>
  )
}

export const CohearingMessageContext = createContext<{
  messages: CohearingMessage[];
  addMessage: (message: CohearingMessage) => void;
  deleteMessage: (messageId: string) => void;
  updateMessage: (messageId: string, update: Partial<CohearingMessage>) => void;
}>(null);